import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { MakeProvider } from '@anecdotes/design-system/form-controls';
import { MultiDropdownControlComponent } from '../multi-dropdown-control/multi-dropdown-control.component';
import { TranslateService } from '@ngx-translate/core';
import { leaveOneSpace, sortAlphabetically } from '@anecdotes/utils';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { LabelType } from '@anecdotes/design-system/utils';
import { DropdownSearchInputComponent } from '../dropdown-search-input/dropdown-search-input.component';

@Component({
  selector: 'app-labels-dropdown',
  templateUrl: './labels-dropdown.component.html',
  styleUrls: ['./labels-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MakeProvider(LabelsDropdownComponent)],
})
export class LabelsDropdownComponent<TData = any> extends MultiDropdownControlComponent implements OnInit, OnChanges, AfterViewInit {
  @HostBinding('class')
  private readonly classes = 'flex';

  @ViewChild('tooltipOnPreview')
  private tooltip: NgbTooltip;

  @ViewChild('dropdownSearch')
  private dropdownSearch: DropdownSearchInputComponent;

  private isAddingShown = false;

  numberOfDisplayedItems: number;

  get partOfHiddenItems(): TData[] {
    return this.selectedLabels.slice(this.numberOfDisplayedItems || 0, this.selectedLabels.length);
  }

  get numberOfHiddenItems(): string | undefined {
    const number = this.partOfHiddenItems.length;

    if (!number) { return; }

    return `+${number}`;
  }

  get isAddOptionShown(): boolean {
    return this.addingEnabled && this.searchField.value && this.isAddingShown;
  }

  get allLabels(): TData[] {
    if (this.sortAllItemsBy) {
      return this.displayedData.sort((item1: TData, item2: TData) => {
        const field1 = this.sortAllItemsBy(item1);
        const field2 = this.sortAllItemsBy(item2);

        return sortAlphabetically(field1, field2);
      });
    }

    return this.displayedData;
  }

  get selectedLabels(): TData[] {
    if (this.sortSelectedItemsBy) {
      return this.selectedItems.sort((item1: TData, item2: TData) => {
        const field1 = this.sortSelectedItemsBy(item1);
        const field2 = this.sortSelectedItemsBy(item2);

        return sortAlphabetically(field1, field2);
      });
    }

    return this.selectedItems;
  }

  @Input()
  selectedData: TData[];

  @Input()
  doesDropdownOverlapsButton = true;

  @Input()
  addingEnabled = false;

  @Input()
  hasAddLabel = false;

  @Input()
  labelIcon: string;

  @Input()
  labelType: LabelType = 'primary';

  @Input()
  displayEmptyState = true;

  @Input()
  displayAllSelectedItems = false;

  @Input()
  displaySelectedItemsFully = false;

  @Input()
  displaySelectedItemsInline = false;

  @Input()
  emptyListPlaceholder: string;

  @Input()
  entityNameText: string;

  @Input()
  buttonTranslationKey: string;

  @Input()
  buttonIcon: string;

  @Input()
  isReadonly = false;

  @Input()
  overlayPanelClass = '';

  @Input()
  maxLength: number;
  
  @Input()
  shouldTruncate = false;

  @Input()
  sortSelectedItemsBy: (item: TData) => string;

  @Input()
  sortAllItemsBy: (item: TData) => string;

  @Output()
  addOption = new EventEmitter<string>();

  @Output()
  attachOption = new EventEmitter<TData>();

  @Output()
  detachOption = new EventEmitter<TData>();

  constructor(
    injector: Injector,
    translate: TranslateService,
    hostElementRef: ElementRef<HTMLElement>,
    cd: ChangeDetectorRef,
  ) {
    super(injector, translate, hostElementRef, cd);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.cd.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);

    const { selectedData } = changes;

    if (selectedData) {
      this.clearSelectedItems();
      this.writeValue(selectedData.currentValue);
    }
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
  }

  itemsTrackByFn(index: number, item: TData): number | string {
    return item ? this.getItemId(item) : index;
  }

  selectItem(item: TData): void {
    this.clearSearch();
    this.attachOption.emit(item);
    this.updateItem(item, true);
  }

  unselectItem(item: TData): void {
    this.detachOption.emit(item);
    this.updateItem(item, false);
  }

  handleSearchInput(): void {
    if (this.addingEnabled) {
      const searchValue = leaveOneSpace(this.searchField.value);
      this.isAddingShown = !!searchValue && !this.hasSearchEqualLabels(searchValue);
    }
  }

  updateOverlayPosition(): void {
    this.cdkConnectedOverlay.overlayRef.updatePosition();
  }

  addNewOption(): void {
    const searchValue = leaveOneSpace(this.searchField.value);
    this.addOption.emit(searchValue);
    this.clearSearch();
  }

  closeTooltip(): void {
    this.tooltip.close();
  }

  changeDisplayedItems(numberOfDisplayedItems: number): void {
    this.numberOfDisplayedItems = numberOfDisplayedItems;
    this.cd.detectChanges();
  }

  focusSearch(): void {
    this.dropdownSearch.focusSearch();
  }

  @HostListener('document:keydown.enter')
  private onEnterClick(): void {
    if (!this.isDropdownOpened) { return; }

    const hasOnlyOneOption = this.displayedData.length === 1 && !this.isAddingShown;

    if (hasOnlyOneOption) {
      this.selectItem(this.displayedData[0]);
      return;
    }

    const hasOnlyNewOption = !this.displayedData.length && this.isAddingShown;

    if (hasOnlyNewOption) {
      this.addNewOption();
    }
  }

  private clearSelectedItems(): void {
    this.dictionary = {};
  }
}
